// Medical history + concomitant meds sections, ported from renderMHSection/
// renderCMSection (source lines 6828-6981) and their add/edit modals
// (openAddMHModal/openAddCMModal etc., lines 7463-7663). Simplified: CM is
// shown as one flat table rather than grouped-by-visit sub-tables (a
// disclosed simplification, not a data-model change - the linkedVisit field
// is still stored and displayed as a column).
import { mhApi, cmApi } from "./api-client.js";
import { escapeHtml, fmtDateOnly, openModal, closeModal, notify, $ } from "./dom.js";
import { MED_ROUTES } from "@woof/shared";

export async function renderMHSection(pid: string, writable: boolean): Promise<string> {
  const list = await mhApi.list(pid);
  const rows = list.length
    ? list
        .map(
          (mh: any) => `
      <tr>
        <td>${escapeHtml(mh.mhterm)}${mh.source ? `<span class="cm-source-tag" title="${escapeHtml(mh.source)}">📋</span>` : ""}</td>
        <td>${escapeHtml(mh.treatment || "")}</td>
        <td>${fmtDateOnly(mh.mhstdat)}</td>
        <td>${mh.ongoing ? '<span class="badge badge-info">En cours</span>' : mh.mhendat ? fmtDateOnly(mh.mhendat) : '<span class="muted">—</span>'}</td>
        <td>${writable ? `<button class="btn btn-sm btn-danger" data-mh-delete="${mh.id}">×</button>` : ""}</td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="muted">Aucun antécédent.</td></tr>`;

  const html = `
    <p class="field-hint">Antécédents médicaux et traitements pré-existants. ${list.length ? `<strong>${list.length} antécédent${list.length > 1 ? "s" : ""}.</strong>` : ""}</p>
    ${writable ? `<button class="btn btn-primary btn-sm" id="btnAddMH">+ Ajouter antécédent</button>` : ""}
    <table class="data-table" style="margin-top:10px">
      <thead><tr><th>Condition</th><th>Traitement</th><th>Début</th><th>Fin / Statut</th><th></th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  return `<div class="section" data-section-id="mh"><div class="section-header"><h4>📋 Antécédents médicaux</h4></div><div class="section-body">${html}</div></div>`;
}

export function attachMHHandlers(root: ParentNode, pid: string, onChange: () => void): void {
  $("#btnAddMH", root)?.addEventListener("click", () => openAddMHModal(pid, onChange));
  root.querySelectorAll<HTMLElement>("[data-mh-delete]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const reason = prompt("Motif de suppression (facultatif) :") ?? undefined;
      await mhApi.remove(pid, btn.dataset.mhDelete!, reason);
      onChange();
    });
  });
}

function openAddMHModal(pid: string, onChange: () => void): void {
  openModal(
    "Ajouter un antécédent médical",
    `<div class="form-grid">
      <div class="form-group" style="grid-column:1/-1"><label>Condition</label><input id="mh_condition"></div>
      <div class="form-group"><label>Date de début</label><input type="date" id="mh_start"></div>
      <div class="form-group"><label>Date de fin</label><input type="date" id="mh_end"></div>
      <div class="form-group"><label class="cbx"><input type="checkbox" id="mh_ongoing"><span>En cours</span></label></div>
      <div class="form-group" style="grid-column:1/-1"><label>Traitement</label><input id="mh_treatment"></div>
    </div>`,
    `<button class="btn btn-secondary" id="mhCancel">Annuler</button><button class="btn btn-primary" id="mhSave">Enregistrer</button>`
  );
  $("#mhCancel")!.addEventListener("click", closeModal);
  $("#mhSave")!.addEventListener("click", async () => {
    const mhterm = ($("#mh_condition") as HTMLInputElement).value.trim();
    if (!mhterm) { notify("error", "La condition est requise."); return; }
    await mhApi.create(pid, {
      mhterm,
      mhstdat: ($("#mh_start") as HTMLInputElement).value || undefined,
      mhendat: ($("#mh_end") as HTMLInputElement).value || undefined,
      ongoing: ($("#mh_ongoing") as HTMLInputElement).checked,
      treatment: ($("#mh_treatment") as HTMLInputElement).value.trim() || undefined,
    });
    closeModal();
    onChange();
  });
}

export async function renderCMSection(pid: string, writable: boolean): Promise<string> {
  const list = await cmApi.list(pid);
  const rows = list.length
    ? list
        .map((cm: any) => {
          const routeLbl = MED_ROUTES.find((r) => r.code === cm.cmroute)?.label ?? cm.cmroute;
          const finCell = cm.ongoing ? '<span class="badge badge-info">En cours</span>' : cm.cmendat ? fmtDateOnly(cm.cmendat) : '<span class="muted">—</span>';
          const actions = writable
            ? `<button class="btn btn-sm btn-secondary" data-cm-edit="${cm.id}" title="Éditer">✏️</button><button class="btn btn-sm btn-danger" data-cm-delete="${cm.id}" title="Supprimer">×</button>`
            : "";
          return `<tr>
            <td>${escapeHtml(cm.cmtrt)}${cm.source ? `<span class="cm-source-tag" title="${escapeHtml(cm.source)}">📋</span>` : ""}</td>
            <td>${escapeHtml(cm.cmdose || "")}</td>
            <td>${escapeHtml(routeLbl || "")}</td>
            <td>${escapeHtml(cm.cmindc || "")}</td>
            <td>${escapeHtml(cm.linkedVisit || "")}</td>
            <td>${fmtDateOnly(cm.cmstdat)}</td>
            <td>${finCell}</td>
            <td style="white-space:nowrap">${actions}</td>
          </tr>`;
        })
        .join("")
    : `<tr><td colspan="8" class="muted">Aucun traitement concomitant.</td></tr>`;

  const html = `
    <p class="field-hint"><strong>Total : ${list.length} traitement${list.length > 1 ? "s" : ""}.</strong></p>
    ${writable ? `<button class="btn btn-primary btn-sm" id="btnAddCM">+ Ajouter un traitement</button>` : ""}
    <table class="data-table" style="margin-top:10px">
      <thead><tr><th>Substance</th><th>Dose</th><th>Voie</th><th>Indication</th><th>Visite liée</th><th>Début</th><th>Fin</th><th></th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  return `<div class="section" data-section-id="cm"><div class="section-header"><h4>💊 Traitements concomitants</h4></div><div class="section-body">${html}</div></div>`;
}

function cmForm(cm: any = {}): string {
  const routeOpts = MED_ROUTES.map((r) => `<option value="${r.code}" ${cm.cmroute === r.code ? "selected" : ""}>${escapeHtml(r.label)}</option>`).join("");
  return `<div class="form-grid">
    <div class="form-group"><label>Substance</label><input id="cm_substance" value="${escapeHtml(cm.cmtrt || "")}"></div>
    <div class="form-group"><label>Dose</label><input id="cm_dose" value="${escapeHtml(cm.cmdose || "")}"></div>
    <div class="form-group"><label>Voie</label><select id="cm_route"><option value="">—</option>${routeOpts}</select></div>
    <div class="form-group"><label>Indication</label><input id="cm_indication" value="${escapeHtml(cm.cmindc || "")}"></div>
    <div class="form-group"><label>Visite liée</label><input id="cm_linked_visit" value="${escapeHtml(cm.linkedVisit || "")}"></div>
    <div class="form-group"><label>Date de début</label><input type="date" id="cm_start" value="${cm.cmstdat || ""}"></div>
    <div class="form-group"><label>Date de fin</label><input type="date" id="cm_end" value="${cm.cmendat || ""}"></div>
    <div class="form-group"><label class="cbx"><input type="checkbox" id="cm_ongoing" ${cm.ongoing ? "checked" : ""}><span>En cours</span></label></div>
    <div class="form-group"><label class="cbx"><input type="checkbox" id="cm_atcd" ${cm.atcd ? "checked" : ""}><span>Antécédent (pré-étude)</span></label></div>
  </div>`;
}

function readCMForm() {
  return {
    cmtrt: ($("#cm_substance") as HTMLInputElement).value.trim(),
    cmdose: ($("#cm_dose") as HTMLInputElement).value.trim() || undefined,
    cmroute: ($("#cm_route") as HTMLSelectElement).value,
    cmindc: ($("#cm_indication") as HTMLInputElement).value.trim() || undefined,
    linkedVisit: ($("#cm_linked_visit") as HTMLInputElement).value.trim() || undefined,
    cmstdat: ($("#cm_start") as HTMLInputElement).value || undefined,
    cmendat: ($("#cm_end") as HTMLInputElement).value || undefined,
    ongoing: ($("#cm_ongoing") as HTMLInputElement).checked,
    atcd: ($("#cm_atcd") as HTMLInputElement).checked,
  };
}

export function attachCMHandlers(root: ParentNode, pid: string, onChange: () => void): void {
  $("#btnAddCM", root)?.addEventListener("click", () => {
    openModal("Ajouter un traitement concomitant", cmForm(), `<button class="btn btn-secondary" id="cmCancel">Annuler</button><button class="btn btn-primary" id="cmSave">Enregistrer</button>`);
    $("#cmCancel")!.addEventListener("click", closeModal);
    $("#cmSave")!.addEventListener("click", async () => {
      const data = readCMForm();
      if (!data.cmtrt || !data.cmroute) { notify("error", "Substance et voie sont requises."); return; }
      await cmApi.create(pid, data);
      closeModal();
      onChange();
    });
  });

  root.querySelectorAll<HTMLElement>("[data-cm-edit]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const list = await cmApi.list(pid);
      const cm = list.find((c: any) => c.id === btn.dataset.cmEdit);
      if (!cm) return;
      openModal("Éditer le traitement", cmForm(cm), `<button class="btn btn-secondary" id="cmCancel">Annuler</button><button class="btn btn-primary" id="cmSave">Enregistrer</button>`);
      $("#cmCancel")!.addEventListener("click", closeModal);
      $("#cmSave")!.addEventListener("click", async () => {
        await cmApi.update(pid, cm.id, readCMForm());
        closeModal();
        onChange();
      });
    });
  });

  root.querySelectorAll<HTMLElement>("[data-cm-delete]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const reason = prompt("Motif de suppression (facultatif) :") ?? undefined;
      await cmApi.remove(pid, btn.dataset.cmDelete!, reason);
      onChange();
    });
  });
}
